import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Button, Field, PhoneFrame, Select, StatusPill, Tabs, Textarea } from '@bb/ui';
import { Fragment, useState } from 'react';
import { ApiError, type AdminApi, type PayloadPreview, type PayloadPreviewBody, type PreviewProfile } from '../../../api/client.ts';
import { environmentLocales, isRtlLocale } from './localization.ts';
import { abortText, CHANNEL_LABELS, contentFields, decodedFcmData, parseJsonObject, pushView, warningText, type DeliveryChannel } from './personalization.ts';
import { DEFAULT_PREVIEW_SEED, ProfileSelector } from './ProfileSelector.tsx';
import { useEnvironmentLocales } from './useEnvironmentLocales.ts';
import { WebhookRequestView } from '../webhook/WebhookRequestView.tsx';
import { useTemplateVariables } from './useTemplateVariables.ts';

/** Trigger data a preview can carry as a JSON sample. */
export type PreviewSample = 'event_properties' | 'api_trigger_properties' | 'context';

export interface PayloadPreviewPanelProps {
  environmentId: string;
  /** Channels the message is previewed in, the first by default; several (Android and iOS push) give a choice. */
  channels: readonly DeliveryChannel[];
  /** The channel content being edited; without it the campaign's draft (else published) variant is previewed. */
  content?: Record<string, unknown>;
  /** The campaign: its trigger and data (and, without `content`, its saved content). */
  campaignId?: string;
  /** The variant of the campaign, the first by default. */
  variantApiId?: string;
  /** Trigger data samples to edit as JSON; by default what the campaign's variables may read. */
  samples?: readonly PreviewSample[];
  /** Who is previewed first; a random user by default. */
  initialProfile?: PreviewProfile;
  /** Section heading and accessible name. */
  title?: string;
}

const SAMPLE_FIELDS: Record<PreviewSample, { label: string; hint: string; key: 'eventProperties' | 'apiTriggerProperties' | 'context' }> = {
  event_properties: { label: 'Event properties (JSON)', hint: 'Empty: the properties of the user’s latest trigger event.', key: 'eventProperties' },
  api_trigger_properties: { label: 'API trigger properties (JSON)', hint: 'What the backend would send with the trigger.', key: 'apiTriggerProperties' },
  context: { label: 'Canvas context (JSON)', hint: 'Context variables of the Canvas step.', key: 'context' },
};
const SAMPLE_ORDER: readonly PreviewSample[] = ['event_properties', 'api_trigger_properties', 'context'];

function errorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'The preview could not be rendered.';
  if (error.code === 'profile_not_found') return 'No such user in this environment: check the external ID, or preview a random user.';
  if (error.code === 'campaign_not_found') return 'The campaign was not found in this environment.';
  if (error.code === 'no_content') return 'The message has no content for this channel yet.';
  if (error.status === 403) return 'Previewing needs the campaigns.edit permission.';
  return error.message;
}

function Facts({ items }: { items: Array<{ label: string; value: string }> }) {
  return (
    <dl className="facts">
      {items.map((f) => (
        <Fragment key={f.label}>
          <dt>{f.label}</dt>
          <dd>{f.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/** «View»: the message as the user would see it, in words (also the push composer's Test tab, BB-73). */
export function HumanView({ channel, result }: { channel: DeliveryChannel; result: PayloadPreview }) {
  if (result.aborted) return <p className="notice notice--warning">{abortText(result.aborted)}</p>;
  const push = pushView(channel, result.payload);
  const masked = result.warnings.some((w) => w.code === 'masked_personal_data');
  if (channel === 'webhook') return <WebhookRequestView payload={result.payload} masked={masked} />;
  const dir = isRtlLocale(result.locale) ? 'rtl' : undefined;
  if (push) {
    // Title, subtitle and text are in the notification; the rest is listed beside it.
    const fields = [
      { label: 'Summary', value: push.summary },
      { label: 'Click action', value: push.action },
      { label: 'Buttons', value: push.buttons.join(' · ') || undefined },
      { label: 'Image URL', value: push.imageUrl },
    ].flatMap((f) => (f.value ? [{ label: f.label, value: f.value }] : []));
    const notification = push.silent ? (
      <p className="hint">Silent push: nothing is shown, the app is woken up in the background.</p>
    ) : (
      <PhoneFrame platform={channel === 'push_ios' ? 'ios' : 'android'}>
        <article className="push-preview" aria-label="Notification" lang={result.locale} dir={dir}>
          {push.title && <p className="push-preview__title">{push.title}</p>}
          {push.subtitle && <p className="push-preview__subtitle">{push.subtitle}</p>}
          {push.body && <p className="push-preview__body">{push.body}</p>}
          {push.imageUrl && <img className="push-preview__image" src={push.imageUrl} alt="Notification image" />}
        </article>
      </PhoneFrame>
    );
    return (
      <div className="payload-preview__view">
        {notification}
        <Facts items={fields} />
        {masked && <p className="hint">Personal data shows as •••.</p>}
      </div>
    );
  }
  const fields = contentFields(channel, result.payload);
  return (
    <div className="payload-preview__view" lang={result.locale} dir={dir}>
      {fields.length ? <Facts items={fields} /> : <p className="hint">No text to show: see the payload JSON.</p>}
      {masked && <p className="hint">Personal data shows as •••.</p>}
    </div>
  );
}

/** «Warnings»: what does not render as written, and why a send would stop. */
export function WarningsView({ result }: { result: PayloadPreview }) {
  const others = result.warnings.filter((w) => w.code !== 'unresolved_variables');
  const nothing = !result.aborted && result.unresolved.length === 0 && result.usedDefaults.length === 0 && others.length === 0;
  return (
    <div className="payload-preview__warnings">
      {result.aborted && (
        <div role="alert">
          <p>
            <StatusPill tone="danger">Not sent</StatusPill> {abortText(result.aborted)}
          </p>
          {result.aborted.detail && (
            <p>
              Reason: <q>{result.aborted.detail}</q>
            </p>
          )}
        </div>
      )}
      {result.unresolved.length > 0 && (
        <section aria-label="Unresolved variables">
          <h4>Unresolved variables</h4>
          <p className="hint">No value for this user and no default: they render empty. Add a default with the personalization button.</p>
          <ul>
            {result.unresolved.map((v) => (
              <li key={v}>
                <code>{v}</code>
              </li>
            ))}
          </ul>
        </section>
      )}
      {result.usedDefaults.length > 0 && (
        <section aria-label="Defaults used">
          <h4>Defaults used</h4>
          <p className="hint">No value for this user: the default was shown.</p>
          <ul>
            {result.usedDefaults.map((v) => (
              <li key={v}>
                <code>{v}</code>
              </li>
            ))}
          </ul>
        </section>
      )}
      {others.length > 0 && (
        <ul aria-label="Warnings">
          {others.map((w) => (
            <li key={`${w.code}/${w.detail ?? ''}`}>
              <StatusPill tone={w.code === 'masked_personal_data' ? 'info' : 'warning'}>{w.code === 'masked_personal_data' ? 'Masked' : 'Warning'}</StatusPill> {warningText(w)}
            </li>
          ))}
        </ul>
      )}
      {nothing && <p className="hint">No warnings: every variable has a value for this user.</p>}
    </div>
  );
}

/**
 * Preview of the exact payload a user would get (BB-54, CMP-010): choose the user (random with a seed, by external
 * ID, or a test user), optionally trigger data samples and a locale, and see it rendered by the delivery code —
 * «View» in words, «Payload JSON» as the provider gets it (FCM message, APNs request, the SDK card), and «Warnings»:
 * unresolved variables, defaults used, an abort, payload size, insecure links, masked personal data.
 */
export function PayloadPreviewPanel({ environmentId, channels, content, campaignId, variantApiId, samples, initialProfile, title = 'Preview as user' }: PayloadPreviewPanelProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [channel, setChannel] = useState<DeliveryChannel>(channels[0] ?? 'push_android');
  const [profile, setProfile] = useState<PreviewProfile | null>(initialProfile ?? { kind: 'random', seed: DEFAULT_PREVIEW_SEED });
  const [locale, setLocale] = useState('');
  const [sampleText, setSampleText] = useState<Partial<Record<PreviewSample, string>>>({});
  const [tab, setTab] = useState('view');
  const locales = useEnvironmentLocales(environmentId);
  const codes = locales ? environmentLocales(locales).codes : [];
  const variables = useTemplateVariables(environmentId, campaignId);
  const shownSamples = samples ?? SAMPLE_ORDER.filter((s) => variables.data?.namespaces.includes(s));
  const current = channels.includes(channel) ? channel : (channels[0] ?? channel);

  const parsed = shownSamples.map((s) => ({ sample: s, ...parseJsonObject(sampleText[s] ?? '') }));
  const invalid = parsed.some((p) => p.error);
  const body: PayloadPreviewBody | null =
    profile && !invalid && (content || campaignId)
      ? {
          channel: current,
          ...(campaignId ? { campaignId } : {}),
          ...(variantApiId ? { variantApiId } : {}),
          ...(content ? { content } : {}),
          profile,
          ...Object.fromEntries(parsed.flatMap((p) => (p.value ? [[SAMPLE_FIELDS[p.sample].key, p.value]] : []))),
          ...(locale ? { locale } : {}),
        }
      : null;
  // The last result stays while the next preview renders.
  const [result, setResult] = useState<PayloadPreview>();
  const preview = useMutation({ mutationFn: (request: PayloadPreviewBody) => api.previewPayload(environmentId, request), onSuccess: setResult, onError: () => setResult(undefined) });
  const [shownFor, setShownFor] = useState<string | null>(null);
  const outdated = result && body && shownFor !== JSON.stringify(body);
  const run = () => {
    if (!body) return;
    setShownFor(JSON.stringify(body));
    preview.mutate(body);
  };
  const warningCount = result ? result.warnings.length + (result.aborted ? 1 : 0) : 0;
  const decoded = result ? decodedFcmData(result.payload) : undefined;

  return (
    <section className="panel payload-preview" aria-label={title}>
      <h3>{title}</h3>
      <div className="stack-form">
        {channels.length > 1 && (
          <Field label="Channel">
            <Select options={channels.map((c) => ({ value: c, label: CHANNEL_LABELS[c] }))} value={current} onChange={(e) => setChannel(e.target.value as DeliveryChannel)} />
          </Field>
        )}
        <ProfileSelector environmentId={environmentId} value={profile} onChange={setProfile} />
        {codes.length > 1 && (
          <Field label="Locale" hint="Another language than the user’s, to check a translation.">
            <Select options={[{ value: '', label: 'The user’s language' }, ...codes.map((c) => ({ value: c, label: c }))]} value={locale} onChange={(e) => setLocale(e.target.value)} />
          </Field>
        )}
        {shownSamples.length > 0 && (
          <fieldset>
            <legend>Trigger data samples</legend>
            {parsed.map((p) => (
              <Field key={p.sample} label={SAMPLE_FIELDS[p.sample].label} hint={SAMPLE_FIELDS[p.sample].hint} error={p.error}>
                <Textarea rows={3} spellCheck={false} value={sampleText[p.sample] ?? ''} placeholder='{"symbol": "EURUSD"}' onChange={(e) => setSampleText((was) => ({ ...was, [p.sample]: e.target.value }))} />
              </Field>
            ))}
          </fieldset>
        )}
        <div className="toolbar">
          <Button variant="primary" onClick={run} loading={preview.isPending} disabled={!body}>
            Preview
          </Button>
          {!content && !campaignId && <span className="hint">Nothing to preview yet.</span>}
        </div>
      </div>
      {preview.isError && <p role="alert">{errorText(preview.error)}</p>}
      {result && !preview.isError && (
        <div className="payload-preview__result" aria-busy={preview.isPending || undefined}>
          <p className="hint" role="status">
            {CHANNEL_LABELS[result.channel as DeliveryChannel] ?? result.channel} for user <code>{result.profileId}</code> in {result.locale}.
            {outdated ? ' The message or the choices changed since: preview again to update it.' : ''}
          </p>
          <Tabs
            label="Preview result"
            value={tab}
            onValueChange={setTab}
            items={[
              { value: 'view', label: 'View', content: <HumanView channel={result.channel as DeliveryChannel} result={result} /> },
              {
                value: 'json',
                label: 'Payload JSON',
                content: (
                  <>
                    <pre className="sdk-log" aria-label="Exact payload">
                      {JSON.stringify(result.payload, null, 2)}
                    </pre>
                    {decoded !== undefined && (
                      <details>
                        <summary>data.bb decoded</summary>
                        <pre className="sdk-log" aria-label="Decoded data.bb">
                          {JSON.stringify(decoded, null, 2)}
                        </pre>
                      </details>
                    )}
                  </>
                ),
              },
              { value: 'warnings', label: warningCount ? `Warnings (${warningCount})` : 'Warnings', content: <WarningsView result={result} /> },
            ]}
          />
        </div>
      )}
    </section>
  );
}
